import React, { useState, useEffect } from "react";
import { faHeart as faSolidHeart } from "@fortawesome/free-solid-svg-icons";
import { faHeart as faRegularHeart } from "@fortawesome/free-regular-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import styles from "./HomeDiscountSection.module.css";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faFire,
  faCartPlus,
  faShoppingCart,
  faEye,
  faStar,
  faChevronLeft,
  faChevronRight,
  faShippingFast,
  faShieldAlt,
} from "@fortawesome/free-solid-svg-icons";
import { likeProduct, unLikeProduct } from "../../service/product";
import { useCart } from "../../context/CartContext";
import CartSidebar from "../CartSidebar";
import { useAuth } from "../../context/AuthContext";
import { Modal, ModalBody } from "react-bootstrap";
import LoginModal from "../login";
import QuickViewModal from "../QuickViewModal";

const HomeDiscountSectionCard = ({ product, index }: any) => {
  const { user } = useAuth();
  const [isLiked, setIsLiked] = useState(product?.hasUpvoted);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showQuickView, setShowQuickView] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isHovering, setIsHovering] = useState(false);
  const navigate = useNavigate();
  const [isCartOpen, setIsCartOpen] = useState(false);
  const { addToCart, removeFromCart, cart, setCart } = useCart();

  const discount =
    product.price > 0
      ? Math.round(((product.price - product.ourPrice) / product.price) * 100)
      : 0;

  const allImages = product?.picture?.map((i: any) => i.path) || [];

  // Auto-play effect when hovering
  useEffect(() => {
    if (!isHovering || allImages.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentImageIndex((prev) =>
        prev === allImages.length - 1 ? 0 : prev + 1
      );
    }, 3000);

    return () => clearInterval(interval);
  }, [isHovering, allImages.length]);

  const toggleLikeProduct = async (productId: any) => {
    try {
      await likeProduct(productId);
      setIsLiked(true);
    } catch (error) {
      console.log("error in liking the product", error);
    }
  };

  const toggleUnlikeProduct = async (productId: any) => {
    try {
      await unLikeProduct(productId);
      setIsLiked(false);
    } catch (error) {
      console.log("error in unliking the product", error);
    }
  };

  const handleAddToCart = (product: any) => {
    addToCart({ ...product, quantity: 1 });
    setIsCartOpen(true);
  };

  const handleUpdateQuantity = (id: any, quantity: any) => {
    setCart(
      cart.map((item) =>
        item.id === id ? { ...item, quantity: Math.max(1, quantity) } : item
      )
    );
  };

  const isInCart = cart?.some(
    (p) => p._id?.toString() === product?._id?.toString()
  );

  const navigateToProduct = () => {
    navigate(`/series/${product?.series?.id}/product/${product?._id}`);
  };

  const handleImageNavigation = (direction: any, e: any) => {
    e.stopPropagation();
    if (direction === "prev") {
      setCurrentImageIndex((prev) =>
        prev === 0 ? allImages.length - 1 : prev - 1
      );
    } else {
      setCurrentImageIndex((prev) =>
        prev === allImages.length - 1 ? 0 : prev + 1
      );
    }
  };

  const handleQuickView = (e: any) => {
    e.stopPropagation();
    setShowQuickView(true);
  };

  // Mock data for features and rating
  const averageRating = 4.5;
  const reviewCount = 12;

  return (
    <>
      <motion.div
        className={styles.productCardWrapper}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: index * 0.1, duration: 0.5 }}
        whileHover={{ y: -8 }}
      >
        <div className={styles.productCard}>
          {/* Enhanced Image Container */}
          <div className={styles.imageContainer}>
            <div className={styles.imageCarousel}>
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentImageIndex}
                  src={allImages[currentImageIndex]}
                  alt={product.name}
                  className={styles.productImage}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5 }}
                />
              </AnimatePresence>

              {/* Navigation Arrows */}
              {allImages.length > 1 && (
                <>
                  <button
                    className={`${styles.navArrow} ${styles.prevArrow}`}
                    onClick={(e) => handleImageNavigation("prev", e)}
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </button>
                  <button
                    className={`${styles.navArrow} ${styles.nextArrow}`}
                    onClick={(e) => handleImageNavigation("next", e)}
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </button>
                </>
              )}

              {/* Top Badges */}
              <div className={styles.topBadges}>
                {discount > 0 && (
                  <div className={styles.discountBadge}>{discount}% OFF</div>
                )}
                <div className={styles.hotBadge}>
                  <FontAwesomeIcon icon={faFire} />
                  Hot Deal
                </div>
              </div>

              {/* Quick Actions */}
              <div className={styles.quickActions}>
                <motion.button
                  className={styles.quickActionBtn}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={async (e) => {
                    e.stopPropagation();
                    !user
                      ? setShowLoginModal(true)
                      : isLiked
                      ? toggleUnlikeProduct(product?._id)
                      : toggleLikeProduct(product?._id);
                  }}
                >
                  <FontAwesomeIcon
                    icon={isLiked ? faSolidHeart : faRegularHeart}
                    className={isLiked ? styles.wishlisted : ""}
                  />
                </motion.button>
                <motion.button
                  className={styles.quickActionBtn}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  onClick={handleQuickView}
                >
                  <FontAwesomeIcon icon={faEye} />
                </motion.button>
              </div>
            </div>

            {/* Carousel Indicators */}
            {allImages.length > 1 && (
              <div className={styles.carouselIndicators}>
                {allImages.map((_: any, idx: any) => (
                  <button
                    key={idx}
                    className={`${styles.indicator} ${
                      idx === currentImageIndex ? styles.activeIndicator : ""
                    }`}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setCurrentImageIndex(idx);
                    }}
                    aria-label={`View image ${idx + 1}`}
                  />
                ))}
              </div>
            )}
          </div>

          {/* Enhanced Product Info */}
          <div className={styles.productInfo}>
            {/* Category Tag */}
            <div className={styles.categoryTag}>
              {product?.series?.title || "Premium Furniture"}
            </div>

            {/* Product Name */}
            <h3 className={styles.productName} onClick={navigateToProduct}>
              {product?.name}
            </h3>

            {/* Rating */}
            <div className={styles.ratingSection}>
              <div className={styles.stars}>
                {[...Array(5)].map((_, i) => (
                  <FontAwesomeIcon
                    key={i}
                    icon={faStar}
                    className={
                      i < Math.floor(averageRating)
                        ? styles.filledStar
                        : styles.emptyStar
                    }
                  />
                ))}
              </div>
              <span className={styles.ratingText}>
                ({averageRating}) • {reviewCount} reviews
              </span>
            </div>

            {/* Features */}
            <div className={styles.features}>
              <div className={styles.feature}>
                <FontAwesomeIcon icon={faShippingFast} />
                <span>Free Shipping</span>
              </div>
              <div className={styles.feature}>
                <FontAwesomeIcon icon={faShieldAlt} />
                <span>1 Year Warranty</span>
              </div>
            </div>

            {/* Pricing */}
            <div className={styles.pricing}>
              {discount > 0 ? (
                <>
                  <span className={styles.originalPrice}>
                    ₹{product?.price?.toLocaleString()}
                  </span>
                  <div className={styles.discountPricing}>
                    <span className={styles.discountedPrice}>
                      ₹{product?.ourPrice?.toLocaleString()}
                    </span>
                    <span className={styles.priceUnit}>/piece</span>
                  </div>
                </>
              ) : (
                <div className={styles.regularPricing}>
                  <span className={styles.regularPrice}>
                    ₹{product?.ourPrice?.toLocaleString()}
                  </span>
                  <span className={styles.priceUnit}>/piece</span>
                </div>
              )}
            </div>

            {/* Save Amount */}
            <div className={styles.saveAmount}>
              You save ₹{(product?.price - product?.ourPrice)?.toLocaleString()}
            </div>

            {/* Progress Bar */}
            <div className={styles.progressBar}>
              <div
                className={styles.progressFill}
                style={{ width: `${Math.min(100, 70 + index * 10)}%` }}
              ></div>
              <span className={styles.stockText}>
                {Math.min(100, 70 + index * 10)}% claimed
              </span>
            </div>

            {/* Action Buttons */}
            <div className={styles.actionButtons}>
              <motion.button
                className={`${styles.addToCartBtn} ${
                  isInCart ? styles.inCart : ""
                }`}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => {
                  isInCart
                    ? removeFromCart(product?._id)
                    : handleAddToCart(product);
                }}
              >
                <FontAwesomeIcon
                  icon={isInCart ? faShoppingCart : faCartPlus}
                />
                {isInCart ? "Remove from Cart" : "Add to Cart"}
              </motion.button>
            </div>
          </div>

          {/* Card Glow Effect */}
          <div className={styles.cardGlow}></div>
        </div>
      </motion.div>

      {/* Quick View Modal */}
      <QuickViewModal
        product={product}
        series={product?.series}
        isOpen={showQuickView}
        onClose={() => setShowQuickView(false)}
        onAddToCart={handleAddToCart}
      />

      <CartSidebar
        isOpen={isCartOpen}
        onClose={() => setIsCartOpen(false)}
        cart={cart}
        onUpdateQuantity={handleUpdateQuantity}
      />

      {showLoginModal && (
        <Modal show={showLoginModal} onHide={() => setShowLoginModal(false)}>
          <ModalBody style={{ padding: "0px", margin: "0px" }}>
            <LoginModal isStandalone={true} />
          </ModalBody>
        </Modal>
      )}
    </>
  );
};

export default HomeDiscountSectionCard;
